<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- Page Header -->
      <div class="page-header">
        <div class="row">
          <div class="col-sm-12">
            <h3 class="page-title">Hồ sơ người dùng</h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item"><a href="#">Dashboard</a></li>
              <li class="breadcrumb-item active">Profile</li>
            </ul>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger">{{ error }}</div>

      <!-- Thông tin tổng quan -->
      <div class="card" v-if="!error">
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col-md-2 text-center">
              <img :src="avatarPreview || avatarSrc"
                   alt="avatar"
                   style="width:100px;height:100px;object-fit:cover;border-radius:50%" />
            </div>

            <div class="col-md-5">
              <h3 class="m-0">{{ profile.hoTen || profile.tenDangNhap || '—' }}</h3>
              <div class="text-muted">{{ profile.phongBan || '—' }}</div>
              <div class="text-muted"><small>{{ profile.chucVu || '—' }}</small></div>
              <div class="mt-2">User ID: <strong>{{ profile.idNguoiDung ?? '—' }}</strong></div>
              <div class="mt-2">
                <span class="badge" :class="statusClass">{{ statusLabel }}</span>
              </div>
            </div>

            <div class="col-md-5">
              <ul class="list-unstyled mb-0">
                <li class="d-flex mb-2">
                  <div class="font-weight-bold" style="min-width:110px">Phone:</div>
                  <div>
                    <a v-if="profile.soDienThoai" :href="telHref">{{ profile.soDienThoai }}</a>
                    <span v-else>—</span>
                  </div>
                </li>
                <li class="d-flex mb-2">
                  <div class="font-weight-bold" style="min-width:110px">Email:</div>
                  <div>
                    <a v-if="profile.email" :href="mailHref">{{ profile.email }}</a>
                    <span v-else>—</span>
                  </div>
                </li>
                <li class="d-flex mb-2">
                  <div class="font-weight-bold" style="min-width:110px">Ngày sinh:</div>
                  <div>{{ profile.ngaySinh || '—' }}</div>
                </li>
                <li class="d-flex mb-2">
                  <div class="font-weight-bold" style="min-width:110px">Giới tính:</div>
                  <div>{{ genderLabel(profile.gioiTinh) }}</div>
                </li>
                <li class="d-flex mb-2">
                  <div class="font-weight-bold" style="min-width:110px">Hôn nhân:</div>
                  <div>{{ maritalLabel(profile.tinhTrangHonNhan) }}</div>
                </li>
                <li class="d-flex mb-2">
                  <div class="font-weight-bold" style="min-width:110px">CCCD:</div>
                  <div>{{ profile.cccd || '—' }}</div>
                </li>
                <li class="d-flex">
                  <div class="font-weight-bold" style="min-width:110px">Địa chỉ:</div>
                  <div>{{ profile.diaChi || '—' }}</div>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="isLoading" class="mt-3">
            <div class="progress">
              <div class="progress-bar progress-bar-striped progress-bar-animated" style="width: 100%"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form chỉnh sửa -->
      <div class="card mt-3" v-if="!error">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Chỉnh sửa thông tin</h5>
          <button class="btn btn-sm btn-outline-secondary" @click="resetForm" :disabled="saving">Hoàn tác</button>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveProfile">
            <div class="row">
              <!-- Cột trái -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Họ tên</label>
                  <input class="form-control" v-model.trim="form.hoTen" :disabled="saving" />
                </div>

                <div class="form-group">
                  <label>Username</label>
                  <input class="form-control" v-model="profile.tenDangNhap" disabled />
                </div>

                <div class="form-group">
                  <label>Email</label>
                  <input class="form-control" type="email" v-model.trim="form.email" :disabled="saving" />
                </div>

                <div class="form-group">
                  <label>Số điện thoại</label>
                  <input class="form-control" v-model.trim="form.soDienThoai" :disabled="saving" />
                </div>

                <div class="form-group">
                  <label>Địa chỉ</label>
                  <textarea class="form-control" rows="2" v-model.trim="form.diaChi" :disabled="saving"></textarea>
                </div>
              </div>

              <!-- Cột phải -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Ảnh đại diện</label>
                  <div class="d-flex align-items-center">
                    <img :src="avatarPreview || avatarSrc"
                         class="mr-3"
                         style="width:72px;height:72px;object-fit:cover;border-radius:50%;border:1px solid #eee" />
                    <div>
                      <input type="file" accept="image/*" @change="onAvatarSelect" :disabled="saving" />
                      <small class="text-muted d-block">JPG/PNG ≤ 5MB</small>
                      <div v-if="uploading" class="text-muted">Đang upload...</div>
                      <button type="button" class="btn btn-link p-0 mt-1" @click="clearAvatar" :disabled="saving || uploading">
                        Xóa ảnh
                      </button>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label>Ngày sinh</label>
                  <input type="date" class="form-control" v-model="form.ngaySinh" :disabled="saving" />
                </div>

                <div class="form-group">
                  <label>Giới tính</label>
                  <select class="form-control" v-model.number="form.gioiTinh" :disabled="saving">
                    <option :value="null">—</option>
                    <option :value="1">Nam</option>
                    <option :value="2">Nữ</option>
                    <option :value="3">Khác</option>
                  </select>
                </div>

                <div class="form-group">
                  <label>Tình trạng hôn nhân</label>
                  <select class="form-control" v-model.number="form.tinhTrangHonNhan" :disabled="saving">
                    <option :value="null">—</option>
                    <option :value="1">Độc thân</option>
                    <option :value="2">Đã kết hôn</option>
                    <option :value="3">Ly hôn</option>
                    <option :value="4">Góa</option>
                  </select>
                </div>

                <div class="form-group">
                  <label>CCCD (9–12 chữ số)</label>
                  <input class="form-control" v-model.trim="form.cccd" :disabled="saving" placeholder="VD: 012345678901" />
                </div>

                <div class="form-group">
                  <label>Mật khẩu mới (tùy chọn)</label>
                  <input class="form-control" type="password" v-model="form.matKhau" :disabled="saving" placeholder="Để trống nếu không đổi" />
                </div>

                <div class="form-group">
                  <label>Xác nhận mật khẩu mới</label>
                  <input class="form-control" type="password" v-model="form.confirmMatKhau" :disabled="saving" />
                </div>
              </div>
            </div>

            <div class="mt-2">
              <small class="text-muted">
                * Nếu nhập mật khẩu mới, phải trùng khớp ở ô xác nhận.
              </small>
            </div>

            <div class="submit-section mt-3">
              <button class="btn btn-primary" :disabled="saving || uploading">
                <span v-if="!saving">Lưu thay đổi</span>
                <span v-else>Đang lưu...</span>
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from '../api'

const isLoading = ref(true)
const error = ref(null)
const saving = ref(false)
const uploading = ref(false)
const avatarPreview = ref(null)

const profile = ref({
  idNguoiDung: null,
  tenDangNhap: '',
  hoTen: '',
  email: '',
  soDienThoai: '',
  chucVu: '',
  phongBan: '',
  trangThai: null,
  avatarId: null,
  avatarUrl: '',
  // mới (hiển thị)
  gioiTinh: null,
  tinhTrangHonNhan: null,
  diaChi: '',
  cccd: '',
  ngaySinh: '' // 'YYYY-MM-DD'
})

const form = ref({
  hoTen: '',
  email: '',
  soDienThoai: '',
  avatarUrl: '',
  matKhau: '',
  confirmMatKhau: '',
  // mới (sửa)
  gioiTinh: null,
  tinhTrangHonNhan: null,
  diaChi: '',
  cccd: '',
  ngaySinh: ''
})

function getToken() {
  return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken') || null
}

function base64UrlDecode(str) {
  try {
    const pad = '='.repeat((4 - (str.length % 4)) % 4)
    const base64 = (str + pad).replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(base64)
    try {
      return decodeURIComponent(decoded.split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''))
    } catch { return decoded }
  } catch { return '' }
}

function parseJwt(token) {
  try { return JSON.parse(base64UrlDecode(token.split('.')[1]) || '{}') } catch { return {} }
}

const avatarSrc = computed(() =>
  profile.value.avatarUrl && profile.value.avatarUrl.trim() !== ''
    ? profile.value.avatarUrl
    : 'assets/img/profiles/avatar-02.jpg'
)

const telHref = computed(() =>
  profile.value.soDienThoai ? `tel:${String(profile.value.soDienThoai).replace(/\s+/g,'')}` : ''
)
const mailHref = computed(() => profile.value.email ? `mailto:${profile.value.email}` : '')

const statusLabel = computed(() => profile.value.trangThai === 1 ? 'Hoạt động' : profile.value.trangThai === 0 ? 'Ngưng hoạt động' : '—')
const statusClass = computed(() => profile.value.trangThai === 1 ? 'badge-success' : profile.value.trangThai === 0 ? 'badge-secondary' : 'badge-light')

function genderLabel(v) {
  if (v === 1) return 'Nam'
  if (v === 2) return 'Nữ'
  if (v === 3) return 'Khác'
  return '—'
}
function maritalLabel(v) {
  if (v === 1) return 'Độc thân'
  if (v === 2) return 'Đã kết hôn'
  if (v === 3) return 'Ly hôn'
  if (v === 4) return 'Góa'
  return '—'
}

async function loadCurrentUser() {
  try {
    isLoading.value = true
    error.value = null

    const token = getToken()
    if (!token) throw new Error('Không tìm thấy access_token. Vui lòng đăng nhập lại.')

    const claims = parseJwt(token)
    const id = claims.idNguoiDung ?? claims.userId ?? claims.id ?? (Number.isInteger(claims.sub) ? claims.sub : null)
    const username = claims.tenDangNhap ?? claims.username ?? (typeof claims.sub === 'string' ? claims.sub : null)

    if (id) {
      const { data } = await axios.get(`/api/nguoi-dung/${id}`)
      mapProfile(data?.data)
      syncForm()
      return
    }

    if (username) {
      const { data } = await axios.get('/api/nguoi-dung', { params: { keyword: username, page: 0, size: 1 } })
      const item = data?.data?.content?.[0]
      if (!item) throw new Error('Không tìm thấy người dùng theo username.')
      mapProfile(item)
      syncForm()
      return
    }

    throw new Error('JWT không có id/username. Cần payload có idNguoiDung hoặc thêm endpoint /me.')
  } catch (e) {
    error.value = e?.response?.data?.message || e.message
    console.error(e)
  } finally {
    isLoading.value = false
  }
}

function mapProfile(d = {}) {
  profile.value = {
    idNguoiDung: d.idNguoiDung ?? null,
    tenDangNhap: d.tenDangNhap ?? '',
    hoTen: d.hoTen ?? '',
    email: d.email ?? '',
    soDienThoai: d.soDienThoai ?? '',
    chucVu: d.chucVu ?? '',
    phongBan: d.phongBan ?? '',
    trangThai: d.trangThai ?? null,
    avatarId: d.avatarId ?? null,
    avatarUrl: d.avatarUrl ?? '',
    // mới
    gioiTinh: d.gioiTinh ?? null,
    tinhTrangHonNhan: d.tinhTrangHonNhan ?? null,
    diaChi: d.diaChi ?? '',
    cccd: d.cccd ?? '',
    ngaySinh: d.ngaySinh ?? ''
  }
}

function syncForm() {
  form.value = {
    hoTen: profile.value.hoTen || '',
    email: profile.value.email || '',
    soDienThoai: profile.value.soDienThoai || '',
    avatarUrl: profile.value.avatarUrl || '',
    matKhau: '',
    confirmMatKhau: '',
    // mới
    gioiTinh: profile.value.gioiTinh,
    tinhTrangHonNhan: profile.value.tinhTrangHonNhan,
    diaChi: profile.value.diaChi || '',
    cccd: profile.value.cccd || '',
    ngaySinh: profile.value.ngaySinh || ''
  }
  avatarPreview.value = null
}

function resetForm() { syncForm() }

/** Upload avatar */
async function uploadImage(file) {
  const fd = new FormData()
  fd.append('files', file)
  fd.append('folderName', 'user')
  const res = await axios.post('/api/images/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  const arr = res?.data
  if (!Array.isArray(arr) || arr.length === 0) throw new Error('Upload thất bại')
  return { url: arr[0].location }
}
async function onAvatarSelect(e) {
  const file = e.target.files?.[0]
  if (!file) return
  uploading.value = true
  try {
    const img = await uploadImage(file)
    form.value.avatarUrl = img.url
    avatarPreview.value = img.url
  } catch (err) {
    alert(err?.response?.data?.message || err.message || 'Upload avatar thất bại')
  } finally { uploading.value = false }
}
function clearAvatar() { form.value.avatarUrl = ''; avatarPreview.value = null }

/** Validate + Save */
function validate() {
  if (form.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    throw new Error('Email không hợp lệ')
  }
  if (form.value.soDienThoai && !/^[0-9+\-\s]{6,20}$/.test(form.value.soDienThoai)) {
    throw new Error('Số điện thoại không hợp lệ')
  }
  if (form.value.cccd && !/^\d{9,12}$/.test(form.value.cccd)) {
    throw new Error('CCCD không hợp lệ (9–12 chữ số)')
  }
  if (form.value.matKhau || form.value.confirmMatKhau) {
    if (form.value.matKhau.length < 6) throw new Error('Mật khẩu mới tối thiểu 6 ký tự')
    if (form.value.matKhau !== form.value.confirmMatKhau) throw new Error('Xác nhận mật khẩu mới không khớp')
  }
}

async function saveProfile() {
  try {
    validate()
    saving.value = true

    const payload = {
      hoTen: form.value.hoTen,
      email: form.value.email,
      soDienThoai: form.value.soDienThoai,
      avatarUrl: form.value.avatarUrl || null,
      gioiTinh: form.value.gioiTinh,
      tinhTrangHonNhan: form.value.tinhTrangHonNhan,
      diaChi: form.value.diaChi,
      cccd: form.value.cccd || null,
      ngaySinh: form.value.ngaySinh || null,
      ...(form.value.matKhau ? { matKhau: form.value.matKhau } : {})
    }

    await axios.put(`/api/nguoi-dung/${profile.value.idNguoiDung}`, payload)
    alert('Cập nhật thành công')
    await loadCurrentUser()
  } catch (e) {
    alert(e?.response?.data?.message || e.message || 'Cập nhật thất bại')
  } finally {
    saving.value = false
  }
}

onMounted(loadCurrentUser)
</script>

<style scoped>
.badge-success { background-color: #28a745; }
.badge-secondary { background-color: #6c757d; }
.badge-light { background-color: #e9ecef; color: #212529; }
</style>
